import type { HingeJoint, Joint, SceneOption, SlideJoint } from "../../types/scene";
import { normalizeSceneOption } from "../../utils/mjcfOptions";
import { logger } from "../../utils/logger";
import { getSceneState } from "../sceneStore";
import {
  DEFAULT_COMPILER_SETTINGS,
  DEFAULT_STATISTIC_SETTINGS,
  normalizeCompilerSettings,
  normalizeStatisticSettings,
} from "../mjcfSettingsStore";
import { arrayToString, formatMjcfNumber, xmlEscapeAttr } from "./xmlGenerationHelpers";

export const VIEWPORT_EXPORT_CAMERA_NAME = "viewport";

export const MJCF_FALLBACK_DEFAULT_CLASS_NAMES = [
  "ur5e",
  "size3",
  "size3_limited",
  "size1",
  "visual",
  "collision",
  "eef_collision",
  "main",
] as const;

export const FALLBACK_DEFAULT_CLASS_NAMES = new Set(MJCF_FALLBACK_DEFAULT_CLASS_NAMES);

export const MJCF_FALLBACK_MATERIAL_DEFAULTS = {
  specular: 0.5,
  shininess: 0.25,
  metallic: 0.0,
  roughness: 0.75,
} as const;

export const buildMjcfFallbackDefaultXml = (tendonDefaultClassXml: string): string => {
  const material = MJCF_FALLBACK_MATERIAL_DEFAULTS;

  return `  <default>
    <default class="ur5e">
      <material specular="${material.specular}" shininess="${material.shininess}" metallic="${material.metallic}" roughness="${material.roughness}"/>
      <joint axis="0 1 0" range="-6.28319 6.28319" armature="0.1"/>
      <general gaintype="fixed" biastype="affine" ctrlrange="-6.2831 6.2831" gainprm="2000" biasprm="0 -2000 -400" forcerange="-150 150"/>
      <default class="size3">
        <default class="size3_limited">
          <joint range="-3.1415 3.1415"/>
          <general ctrlrange="-3.1415 3.1415"/>
        </default>
      </default>
      <default class="size1">
        <general gainprm="500" biasprm="0 -500 -100" forcerange="-28 28"/>
      </default>
      <default class="visual">
        <geom type="mesh" contype="0" conaffinity="0" group="2"/>
      </default>
      <default class="collision">
        <geom type="capsule" group="3"/>
        <default class="eef_collision">
          <geom type="cylinder"/>
        </default>
      </default>
      <site size="0.001" rgba="0.5 0.5 0.5 0.3" group="4"/>
    </default>
${tendonDefaultClassXml ? `${tendonDefaultClassXml}\n` : ""}
  </default>`;
};

export const isScalarJoint = (joint: Joint): joint is HingeJoint | SlideJoint => {
  return joint.kind === "hinge" || joint.kind === "slide";
};

export const isConveyorBeltJointName = (name: unknown): boolean => {
  const value = typeof name === "string" ? name : "";
  return value.includes("conveyor_belt_segment_") && value.includes("_slide");
};

export const extractDefaultClassNamesFromXml = (xml: string | null | undefined): Set<string> => {
  const names = new Set<string>();
  if (!xml) return names;
  Array.from(String(xml).matchAll(/<\s*default\b[^>]*\bclass\s*=\s*"([^"]+)"/gi)).forEach(
    (match) => {
      const name = String(match[1] || "").trim();
      if (name) names.add(name);
    }
  );
  return names;
};

export const getSceneOptionForExport = (sceneState: ReturnType<typeof getSceneState>) =>
  normalizeSceneOption(sceneState.option);

export const getSceneMjcfCompilerForExport = (sceneState: ReturnType<typeof getSceneState>) =>
  normalizeCompilerSettings(sceneState.mjcfCompiler || DEFAULT_COMPILER_SETTINGS);

export const getSceneMjcfStatisticForExport = (sceneState: ReturnType<typeof getSceneState>) =>
  normalizeStatisticSettings(sceneState.mjcfStatistic || DEFAULT_STATISTIC_SETTINGS);

export const normalizeMeshFileForMjcf = (file: string): string =>
  String(file || "")
    .trim()
    .replace(/\\/g, "/")
    .replace(/^\/+/, "")
    .replace(/^assets\//i, "");

export const getExportIntegratorName = (
  requestedIntegrator: string,
  joints: Joint[],
  logPrefix: string
) => {
  const hasBallJoints = joints.some((joint) => joint.kind === "ball");
  if (hasBallJoints) {
    logger.debug(`${logPrefix} Ball joints detected; using Euler integrator`);
    return "Euler";
  }
  return requestedIntegrator;
};

export const buildMjcfOptionXml = (sceneOption: SceneOption, integratorName: string): string => {
  const gravity = arrayToString(sceneOption.gravity);
  const wind = arrayToString(sceneOption.wind);
  const attrs = [
    `integrator="${integratorName}"`,
    `gravity="${gravity}"`,
    `wind="${wind}"`,
    `timestep="${formatMjcfNumber(sceneOption.timestep)}"`,
    `iterations="${sceneOption.iterations}"`,
    `tolerance="${formatMjcfNumber(sceneOption.tolerance)}"`,
    `solver="${sceneOption.solver}"`,
    `cone="${sceneOption.cone}"`,
    `jacobian="${sceneOption.jacobian}"`,
    `impratio="${formatMjcfNumber(sceneOption.impratio)}"`,
    `noslip_iterations="${sceneOption.noslip_iterations}"`,
    `noslip_tolerance="${formatMjcfNumber(sceneOption.noslip_tolerance)}"`,
    `ccd_iterations="${sceneOption.mpr_iterations}"`,
    `ccd_tolerance="${formatMjcfNumber(sceneOption.mpr_tolerance)}"`,
    `density="${formatMjcfNumber(sceneOption.density)}"`,
    `viscosity="${formatMjcfNumber(sceneOption.viscosity)}"`,
    `o_margin="${formatMjcfNumber(sceneOption.o_margin)}"`,
    `o_solref="${arrayToString(sceneOption.o_solref)}"`,
    `o_solimp="${arrayToString(sceneOption.o_solimp)}"`,
  ];
  const optionFlags = sceneOption.flags || {};
  const flagAttrs = Object.entries(optionFlags)
    .filter(([name, value]) => String(name).trim().length > 0 && String(value).trim().length > 0)
    .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
    .map(([name, value]) => `${name}="${xmlEscapeAttr(String(value))}"`)
    .join(" ");
  if (!flagAttrs) {
    return `<option ${attrs.join(" ")}/>`;
  }
  return `<option ${attrs.join(" ")}><flag ${flagAttrs}/></option>`;
};
